import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useWebSocket from '../hooks/useWebSocket';
import {
  appendMessageToConversationCache,
  isInboxNotification,
  notificationKeys,
} from '../hooks/api';
import { useAuth } from './AuthContext';
import { ROUTES } from '../constants/routes';

const WebSocketContext = createContext({
  connectionStatus: 'disconnected',
  sendMessage: () => false,
  subscribe: () => () => {},
  setActiveConversationId: () => {},
});

export function WebSocketProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const onMessagesPage = location.pathname === ROUTES.MESSAGES;
  const queryClient = useQueryClient();
  const listenersRef = useRef(new Set());
  const onMessagesPageRef = useRef(onMessagesPage);
  const activeConversationIdRef = useRef(null);

  React.useEffect(() => {
    onMessagesPageRef.current = onMessagesPage;
  }, [onMessagesPage]);

  const setActiveConversationId = useCallback((id) => {
    activeConversationIdRef.current = id || null;
  }, []);

  const handleMessage = useCallback(
    (payload) => {
      listenersRef.current.forEach((fn) => {
        try {
          fn(payload);
        } catch (_) {
          /* ignore listener errors */
        }
      });

      const type = payload?.type;
      const data = payload?.data || {};

      if (type === 'new_message') {
        const message = data.message;
        const conversationId = data.conversation_id || message?.conversation_id;
        if (conversationId && message) {
          queryClient.setQueryData(['conversationMessages', conversationId], (old) =>
            appendMessageToConversationCache(old, message)
          );
          const isGiphy =
            typeof message.image_url === 'string' &&
            message.image_url.includes('giphy.com');
          const previewText =
            message.content ||
            (message.type === 'image' ? (isGiphy ? 'GIF' : '📷 Image') : '');
          queryClient.setQueryData(['conversations'], (old) => {
            if (!Array.isArray(old)) return old;
            const exists = old.some((c) => c.id === conversationId);
            const viewingThis =
              onMessagesPageRef.current &&
              activeConversationIdRef.current === conversationId;
            const updated = exists
              ? old.map((c) => {
                  if (c.id !== conversationId) return c;
                  const isOwn = message.sender_id === user?.id;
                  const bumpUnread = !isOwn && !viewingThis;
                  return {
                    ...c,
                    updated_at: message.created_at,
                    last_message: {
                      text: previewText,
                      sender_id: message.sender_id,
                      sent_at: message.created_at,
                      type: message.type,
                    },
                    unread_count: bumpUnread
                      ? (c.unread_count || 0) + 1
                      : isOwn || viewingThis
                        ? 0
                        : c.unread_count || 0,
                  };
                })
              : old;
            // Unknown conversation (e.g. just accepted) — refresh list
            if (!exists) {
              queryClient.invalidateQueries({ queryKey: ['conversations'] });
              return old;
            }
            return updated.sort(
              (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
            );
          });
          queryClient.invalidateQueries({ queryKey: ['chatUnreadCount'] });
        }

        if (
          message &&
          message.sender_id !== user?.id &&
          !onMessagesPageRef.current &&
          message.type !== 'system'
        ) {
          const isGiphy =
            typeof message.image_url === 'string' &&
            message.image_url.includes('giphy.com');
          const preview =
            message.type === 'image'
              ? isGiphy
                ? 'Sent a GIF'
                : 'Sent an image'
              : (message.content || '').slice(0, 80);
          toast(`${message.sender_name || 'Someone'}: ${preview}`, {
            icon: '💬',
            duration: 4000,
          });
        }
      }

      if (type === 'presence') {
        const presenceUserId = data.user_id;
        if (presenceUserId) {
          queryClient.setQueryData(['conversations'], (old) => {
            if (!Array.isArray(old)) return old;
            return old.map((c) => {
              if (c.other_user?.id !== presenceUserId) return c;
              return {
                ...c,
                other_user: {
                  ...c.other_user,
                  is_online: !!data.online,
                  last_seen: data.last_seen || c.other_user.last_seen,
                  chat_last_seen: data.last_seen || c.other_user.chat_last_seen,
                },
              };
            });
          });
        }
      }

      if (type === 'request_received' || type === 'request_accepted' || type === 'request_declined') {
        queryClient.invalidateQueries({ queryKey: ['incomingChatRequests'] });
        queryClient.invalidateQueries({ queryKey: ['outgoingChatRequests'] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        if (type === 'request_received' && !onMessagesPageRef.current) {
          const req = data.request || {};
          toast(`${req.from_user_name || 'Someone'} sent you a chat request`, {
            icon: '📩',
          });
        }
      }

      if (type === 'conversation_deleted') {
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        queryClient.invalidateQueries({ queryKey: ['chatUnreadCount'] });
      }
      if (type === 'message_edited' || type === 'message_deleted') {
        const conversationId = data.conversation_id;
        if (conversationId) {
          queryClient.invalidateQueries({ queryKey: ['conversationMessages', conversationId] });
        }
      }

      if (type === 'read_receipt') {
        queryClient.invalidateQueries({ queryKey: ['chatUnreadCount'] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }

      if (type === 'notification') {
        // A refetch racing an in-flight optimistic update would flash stale rows;
        // the mutation's own onSettled refetch picks this change up instead.
        if (!queryClient.isMutating({ mutationKey: notificationKeys.all })) {
          queryClient.invalidateQueries({ queryKey: notificationKeys.all });
        }
        if (isInboxNotification(data)) {
          queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
          queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
        }
      }

      // Spotify listening presence is consumed via subscribe() in SpotifyContext
    },
    [queryClient, user?.id]
  );

  const { connectionStatus, sendMessage, reconnect } = useWebSocket({
    enabled: !!isAuthenticated,
    onMessage: handleMessage,
  });

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const value = useMemo(
    () => ({
      connectionStatus,
      sendMessage,
      subscribe,
      reconnect,
      setActiveConversationId,
    }),
    [connectionStatus, sendMessage, subscribe, reconnect, setActiveConversationId]
  );

  return (
    <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  return useContext(WebSocketContext);
}
